import { ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { AccountCreation } from '@/components/signup/account-creation/account-creation';
import { Input } from '@/components/signup/account-creation/components/input/input';
import { Login } from '@/components/signup/account-creation/components/login/login';
import { Social } from '@/components/signup/account-creation/components/social/social';
import { Submit } from '@/components/signup/account-creation/components/submit/submit';
import { fontSize } from '@/components/signup/utils/font-size';
import { getLoginUrl } from '@/components/signup/utils/url';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocation } from '@/utils/location-provider';

import { AnimatedSocialProof } from '../components/animated-social-proof';

export const PaidAccountCreation = ({
	darkSolidTheme,
	showFourSocialButtons,
	pageId,
}: {
	darkSolidTheme?: boolean;
	showFourSocialButtons?: boolean;
	pageId?: string | undefined;
}) => {
	const { product } = useUserContext();
	const i18n = useIntl();
	const location = useLocation();

	const logInLink = (text: ReactNode) => (
		<Link
			text={text as unknown as string}
			aria-label={text as unknown as string}
			type="inline"
			href={getLoginUrl(location)}
		/>
	);

	const enableCustomerServiceManagementVariant = pageId === 'anchor-lp-jsm-csm';

	const loginText = i18n.formatMessage(
		{
			id: 'BxpUiSignup.SignupForm.existingProduct.confluence.embedded',
			defaultMessage: 'Trying to access {product}? <logInLink>Log in</logInLink>',
		},
		{
			product: enableCustomerServiceManagementVariant
				? 'Customer Service Management'
				: product?.description,
			logInLink,
		},
	);

	return (
		<AccountCreation
			Input={
				<div css={[darkSolidTheme && whiteTextCss, !darkSolidTheme && darkGrayTextCss]}>
					<Input />
				</div>
			}
			Login={
				<div css={[loginSocialProofCss, darkSolidTheme && whiteLinkCss]}>
					{enableCustomerServiceManagementVariant ? (
						<p css={existingProductCss}>
							Join the 300K+ customers that trust Atlassian to power team collaboration
						</p>
					) : (
						<AnimatedSocialProof />
					)}
					<Login loginText={loginText} />
				</div>
			}
			Social={
				<div css={[showFourSocialButtons && socialWrapperStyle]}>
					{!showFourSocialButtons && (
						<Social
							providers={{
								google: 'Google',
								microsoft: 'Microsoft',
							}}
						/>
					)}
					{showFourSocialButtons && <Social />}
				</div>
			}
			Submit={
				<div css={[darkSolidTheme && yellowButtonCss]}>
					<Submit />
				</div>
			}
		/>
	);
};
const socialWrapperStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	ul: {
		gridTemplateColumns: 'repeat(4, 1fr)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '40px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		display: 'none',
	},
});

const loginSocialProofCss = css({
	// only need top padding when social proof is present
	[media.above.md]: {
		paddingBlockStart: '2rem',
	},
});

const yellowButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		backgroundColor: wacToken('color.extended.saffron400'),
		color: `${token('color.text')} !important`,
		['&:hover']: {
			// eslint-disable-next-line local/ensure-wac-token-usage
			backgroundColor: '#FF8B00',
			color: `${token('color.text')} !important`,
		},
	},
});

const whiteLinkCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		color: `${wacToken('color.neutral.light0')} !important`,
	},
});

const whiteTextCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"div[role='note'], div[role='alert']": {
		color: `${wacToken('color.neutral.light0')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'#account_creation_email_help_text': {
		color: wacToken('color.neutral.light0'),
	},
});

const darkGrayTextCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"div[role='note']": {
		// eslint-disable-next-line local/ensure-wac-token-usage
		color: '#44546F',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"div[role='alert']": {
		color: `${token('color.icon.accent.red')} !important`,
	},
});

const existingProductCss = css({
	fontSize: fontSize.lg,
	margin: '2rem 0 0 !important',
	textAlign: 'left',
});
